import { Component, Inject, Injector, Input } from '@angular/core';
import { SnmpAlertTypes } from '@modules/snmp-custom-notifications/store/alert/model';
import * as SnmpDeviceTypeSelectors from '@modules/snmp-custom-notifications/store/alert/selectors';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { WizardStepComponent } from 'mbs-ui-kit';
import { NotificationPlanRule, NotifyToPlatform } from 'mbs-ui-shared/src/lib/models/Notification';
import { AdministratorsFacade } from 'mbs-ui-shared@facades/administrators.facade';
import {
  CustomAlertingSettings,
  NotifyTimeTypeTranslationKeys,
  PorfileRecipientTypes,
  ProfilePostActions,
  RMMAlertTypes,
  RmmAlertingGeneralAlerts,
  RmmAlertingWindowsAlerts
} from 'mbs-ui-shared@models/rmm-custom-notification-model';
import * as ScriptLibrarySelectors from 'mbs-ui-shared@store/rmmScriptLibrary/selectors';
import { getTemperatureInFahrenheit } from 'mbs-ui-shared@utils/temperature';
import { combineLatest, filter, map } from 'rxjs';
import { LoadRmmCustomNotificationWizardService } from '../../utility/load-rmm-custom-notification-wizard.service';

@UntilDestroy()
@Component({
  selector: 'mbs-utility-alert-summary',
  templateUrl: './utility-alert-summary.component.html'
})
export class UtilityAlertSummaryComponent {
  @Input() showAlertsOnly = false;
  @Input() title = this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.utilitySteps.defaultTitle');
  @Input() titleForWinAlerts = this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.utilitySteps.winAlertTitle');
  @Input() profileMode = false;
  @Input() providerEmail: string;

  public resultInfo: Partial<CustomAlertingSettings>;
  public keyTitleList = [];
  public profilePostAction = null;
  public allCustomForProfile = true;
  public canShowPostActions = false;
  public showGeneralAlerts = false;
  public showWindowsAlerts = false;
  public windowsAlerts = RmmAlertingWindowsAlerts;
  public generalAlerts = RmmAlertingGeneralAlerts;
  public snmpData: (SnmpAlertTypes & { deviceTypeName: string })[] = [];

  private readonly minutesTimeType = this.i18n.transform(NotifyTimeTypeTranslationKeys.Minutes);
  private readonly hoursTimeType = this.i18n.transform(NotifyTimeTypeTranslationKeys.Hours);
  private readonly commonKey = 'general';

  private excludeTitleList = ['recipient', 'pushRecipient', 'pushNotification'];
  private keysToBeIgnored = [
    'updateDeferNumber',
    'updateDeferType',
    'updateDeferNumberDiff',
    'updateDeferTypeDiff',
    'excludeBootVolume',
    'enabled',
    'warningPercent',
    'alertPercentMin',
    'alertHighTemperatureMin'
  ];
  private timeKeyList = ['alertWrongSystemTimeDifference', 'duration'];
  private alertText = 'alertpercent';
  private temperatureParameterName = 'alertHighTemperature';
  private notifyToPlatformParamterName = 'notifyToPlatform';
  private keyListByArrayValue = {};
  private arrayToStringJoiner = ', ';

  constructor(
    @Inject(WizardStepComponent) protected parent: WizardStepComponent,
    protected injector: Injector,
    private i18n: I18NextPipe,
    private adminFacade: AdministratorsFacade,
    private loadRmmCustomNotificationWizardService: LoadRmmCustomNotificationWizardService,
    private store: Store
  ) {
    this.store
      .select(SnmpDeviceTypeSelectors.selectStoreData)
      .pipe(
        map((data) =>
          data.alertTypes.map((item) => ({
            ...item,
            deviceTypeName: !item.snmp_device_type_id
              ? this.i18n.transform('mbs-ui:notifications.snmpAllTypes')
              : data.deviceTypes.find((device) => device.id === item.snmp_device_type_id)?.name
          }))
        )
      )
      .subscribe((data) => (this.snmpData = data));

    combineLatest([
      this.loadRmmCustomNotificationWizardService.notificationsToSave,
      this.loadRmmCustomNotificationWizardService.profileActionPayload
    ])
      .pipe(
        filter(([settings, postActions]) => !!settings),
        untilDestroyed(this)
      )
      .subscribe(([settings, postActions]) => {
        this.setupIncomeSettings(settings);
        this.postActionSetup(postActions);
      });

    this.loadRmmCustomNotificationWizardService.profileActionPayload.pipe(untilDestroyed(this)).subscribe((value) => {});
  }

  postActionSetup(postActions: ProfilePostActions) {
    if (!postActions || this.showAlertsOnly) return;

    this.canShowPostActions = false;
    this.profilePostAction = cloneDeep(postActions);

    if (this.profilePostAction) {
      this.allCustomForProfile = !(this.commonKey in this.profilePostAction && this.profilePostAction[this.commonKey] !== null);
      Object.keys(this.profilePostAction).forEach((key) => {
        this.profilePostAction[key] = {
          ...this.profilePostAction[key],
          showProvider: this.showProvider(this.profilePostAction[key]),
          showEmailRecipients: this.showEmailRecipients(this.profilePostAction[key]),
          showPushRecipients: this.showPushRecipients(this.profilePostAction[key]),
          pushRecipientNamesStream$: this.getPushRecipientNamesStream(this.profilePostAction[key])
        };
      });

      this.canShowPostActions = true;
    }
  }

  public setupIncomeSettings(settings: Partial<CustomAlertingSettings>) {
    this.resultInfo = settings;
    this.keyTitleList = Object.keys(settings)
      .filter((key) => !this.excludeTitleList.includes(key))
      .map((key) => key);

    this.showGeneralAlerts = false;
    this.showWindowsAlerts = false;

    this.keyTitleList.forEach((item) => {
      this.keyListByArrayValue = Object.assign(this.keyListByArrayValue, {
        [item]: this.getListByArrayValue(item)
      });
    });
  }

  // hack to optimise output from response
  public getListByArrayValue(key: RMMAlertTypes) {
    return Object.keys(this.resultInfo[key]).flatMap((subKey) => {
      if (this.keysToBeIgnored.includes(subKey)) return [];

      if (this.generalAlerts.includes(key) && this.resultInfo[key]?.enabled) this.showGeneralAlerts = true;
      if (this.windowsAlerts.includes(key) && this.resultInfo[key]?.enabled) this.showWindowsAlerts = true;

      if (key === RMMAlertTypes.SnmpDevice) return [`${this.getSnmpAlertTypeNames(this.getSplittedData(this.resultInfo[key][subKey]))}`];

      if (key === RMMAlertTypes.ServiceState)
        return this.timeKeyList.includes(subKey) ? [] : [`${this.getServicesAndDaemonsNames(this.getSplittedData(this.resultInfo[key]))}`];

      if (this.timeKeyList.includes(subKey)) return [`${this.getItemName(subKey)} ${this.getTime(this.resultInfo[key][subKey])}`];

      if (subKey.toLowerCase().includes(this.alertText))
        return [`${this.getItemName(subKey)} ${this.getSplittedData(this.resultInfo[key][subKey])}%`];

      if (subKey === this.temperatureParameterName)
        return [`${this.getItemName(subKey)} ${this.getTemperatureDataText(this.resultInfo[key][subKey])}`];

      if (subKey === this.notifyToPlatformParamterName)
        return [`${this.getItemName(subKey)} ${this.resultInfo[key][subKey] == NotifyToPlatform.ServersOnly ? false : true}`];

      return this.getSplittedData(this.resultInfo[key][subKey])
        ? [`${this.getItemName(subKey)} ${this.getSplittedData(this.resultInfo[key][subKey])}`]
        : [];
    });
  }

  getSplittedData(data: any) {
    if (!data) return '';

    if (typeof data === 'string') return data.replaceAll(',', this.arrayToStringJoiner);

    if (Array.isArray(data)) return data.length ? data.join(this.arrayToStringJoiner) : '';

    return data;
  }

  public getTime(time: string) {
    const hmsSplitter = ':';
    const daysSplitter = '.';

    const [hours, minutes, seconds] = time.split(hmsSplitter);

    if (hours.includes(daysSplitter)) {
      const [days, newHours] = hours.split(daysSplitter);

      return `${Number(days) * 24 + Number(newHours)} ${this.hoursTimeType.toLowerCase()}`;
    }

    if (!Number(hours) && !Number(minutes)) return '';

    if (Number(hours) > 0) return `${Number(hours)} ${this.hoursTimeType.toLowerCase()}`;
    return `${Number(minutes)} ${this.minutesTimeType.toLowerCase()}`;
  }

  public getItemName(key: string) {
    return `${this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.inSummaryKeys.' + key)}`;
  }

  public getSnmpAlertTypeNames(selectedIds: string) {
    return this.snmpData
      ?.filter((item) => selectedIds?.includes(item.alert_type_guid))
      .map((device) => device?.name)
      .join(this.arrayToStringJoiner);
  }

  public getServicesAndDaemonsNames(data: any) {
    if (!data?.includeServices) return '';

    return data?.includeServices?.map((service) => service?.serviceDisplayName ?? service?.serviceName).join(this.arrayToStringJoiner);
  }

  public getTemperatureDataText(thresholdValue: string) {
    return `${thresholdValue}\u00B0C (${getTemperatureInFahrenheit(thresholdValue)}\u00B0F)`;
  }

  // profile part utility
  getScriptNameStream(id: string) {
    return this.store.select(ScriptLibrarySelectors.selectEntry(id)).pipe(
      filter((value) => !!value),
      map((value) => value?.name)
    );
  }

  showProvider(item: any) {
    return [
      NotificationPlanRule.Full,
      NotificationPlanRule.Provider,
      NotificationPlanRule.Provider ^ NotificationPlanRule.SpecifiedEmails,
      PorfileRecipientTypes.Provider,
      PorfileRecipientTypes.ProviderAndEmails
    ].includes(item?.recipient?.type);
  }

  showEmailRecipients(item: any) {
    return (
      [
        NotificationPlanRule.Provider ^ NotificationPlanRule.SpecifiedEmails,
        NotificationPlanRule.SpecifiedEmails,
        NotificationPlanRule.Users,
        PorfileRecipientTypes.ProviderAndEmails,
        PorfileRecipientTypes.Administrators,
        PorfileRecipientTypes.Users,
        PorfileRecipientTypes.SpecifiedEmails
      ].includes(item?.recipient?.type) && item?.recipient?.emails?.some((value) => value !== this.providerEmail)
    );
  }

  showPushRecipients(item: any) {
    return item?.pushNotification?.enabled;
  }

  getPushRecipientNamesStream(item: any) {
    // already loaded on previous steps
    return this.adminFacade.getDataWhenLoaded$.pipe(
      map((adminList) => adminList.filter((admin) => item.pushRecipient.users.some((id) => admin.id === id)).map((admin) => admin.email)),
      untilDestroyed(this)
    );
  }

  canShowThresholdInCustom(item: string) {
    if (!this.profilePostAction) return false;

    return (
      this.profilePostAction[item]?.showProvider ||
      this.profilePostAction[item]?.showEmailRecipients ||
      this.profilePostAction[item]?.showPushRecipients ||
      Object.keys(this.keyListByArrayValue[item]).some((key) => !!this.keyListByArrayValue[item][key])
    );
  }
}
